import { getAdminHeaders, logout } from './adminAuth';

const BASE_URL = (
  process.env.REACT_APP_AUTH_API_BASE_URL ||
  process.env.REACT_APP_API_BASE_URL ||
  'https://apidev.chemrobotics.com'
).replace(/\/$/, '');

function buildQuery(params) {
  const q = new URLSearchParams();
  Object.entries(params).forEach(([k, v]) => {
    if (v !== undefined && v !== null && v !== '') q.set(k, v);
  });
  return q.size ? `?${q}` : '';
}

function apiErrorMessage(payload) {
  if (payload?.message) return payload.message;
  const fields = payload?.data?.fields || payload?.fields || payload?.errors;
  if (Array.isArray(fields)) return fields.filter(Boolean).join('. ');
  if (fields && typeof fields === 'object') {
    return Object.entries(fields)
      .flatMap(([field, messages]) => (Array.isArray(messages) ? messages : [messages]).map((message) => `${field}: ${message}`))
      .filter(Boolean)
      .join('. ');
  }
  return 'Request failed. Please try again.';
}

async function request(path, options = {}) {
  const { headers: extraHeaders, ...rest } = options;
  const res = await fetch(`${BASE_URL}${path}`, {
    ...rest,
    headers: { ...getAdminHeaders(), ...extraHeaders },
  });
  const payload = await res.json().catch(() => ({}));
  if (!res.ok || payload.status === false) {
    if (res.status === 401) { logout(); throw new Error('Session expired. Please login again.'); }
    if (payload.code === 'PERMISSION_DENIED' || res.status === 403) {
      throw new Error('Your account does not have admin API access. Contact the ChemRobotics team to enable admin permissions for your account.');
    }
    throw new Error(apiErrorMessage(payload));
  }
  return payload.data ?? payload;
}

/* ── Admin Profile ─────────────────────────────────────────── */
export function getAdminProfile() {
  return request('/admin/me');
}

/* ── Categories ────────────────────────────────────────────── */
export async function getAdminCategories(params = {}) {
  const { limit, ...rest } = params;
  const queryParams = { ...rest };
  if (limit) queryParams.pageSize = limit;
  const data = await request(`/admin/chatbot/categories${buildQuery(queryParams)}`);
  const categories = data.categories || [];
  const pg = data.pagination || {};
  return {
    categories,
    total: pg.total ?? data.total ?? categories.length,
    page: pg.page ?? data.page ?? 1,
    pageSize: pg.pageSize ?? data.pageSize ?? 10,
    totalPages: pg.totalPages ?? data.totalPages ?? Math.ceil((pg.total ?? data.total ?? categories.length) / (pg.pageSize ?? data.pageSize ?? 10)),
  };
}

export function createCategory(body) {
  return request('/admin/chatbot/categories', {
    method: 'POST',
    body: JSON.stringify(body),
  });
}

export function updateCategory(id, body) {
  return request(`/admin/chatbot/categories/${encodeURIComponent(id)}`, {
    method: 'PUT',
    body: JSON.stringify(body),
  });
}

export function patchCategoryStatus(id, status) {
  return request(`/admin/chatbot/categories/${encodeURIComponent(id)}/status`, {
    method: 'PATCH',
    body: JSON.stringify({ status }),
  });
}

export function deleteCategory(id) {
  return request(`/admin/chatbot/categories/${encodeURIComponent(id)}`, {
    method: 'DELETE',
  });
}

/* ── Questions ─────────────────────────────────────────────── */
export async function getAdminQuestions(params = {}) {
  const { limit, ...rest } = params;
  const queryParams = { ...rest };
  if (limit) queryParams.pageSize = limit;
  const data = await request(`/admin/chatbot/questions${buildQuery(queryParams)}`);
  const questions = data.questions || [];
  const pg = data.pagination || {};
  return {
    questions,
    total: pg.total ?? data.total ?? questions.length,
    page: pg.page ?? data.page ?? 1,
    pageSize: pg.pageSize ?? data.pageSize ?? 10,
    totalPages: pg.totalPages ?? data.totalPages ?? Math.ceil((pg.total ?? data.total ?? questions.length) / (pg.pageSize ?? data.pageSize ?? 10)),
  };
}

export function getAdminQuestion(id) {
  return request(`/admin/chatbot/questions/${encodeURIComponent(id)}`);
}

export function createQuestion(body) {
  return request('/admin/chatbot/questions', {
    method: 'POST',
    body: JSON.stringify(body),
  });
}

export function updateQuestion(id, body) {
  return request(`/admin/chatbot/questions/${encodeURIComponent(id)}`, {
    method: 'PUT',
    body: JSON.stringify(body),
  });
}

export function patchQuestionStatus(id, status) {
  return request(`/admin/chatbot/questions/${encodeURIComponent(id)}/status`, {
    method: 'PATCH',
    body: JSON.stringify({ status }),
  });
}

export function deleteQuestion(id) {
  return request(`/admin/chatbot/questions/${encodeURIComponent(id)}`, {
    method: 'DELETE',
  });
}

export function getChatbotAdminSummary() {
  return request('/admin/chatbot/summary');
}

export function getQueryConfig(questionId) {
  return request(`/admin/chatbot/questions/${encodeURIComponent(questionId)}/query-config`);
}

export function updateQueryConfig(questionId, body) {
  return request(`/admin/chatbot/questions/${encodeURIComponent(questionId)}/query-config`, {
    method: 'PUT',
    body: JSON.stringify(body),
  });
}

export function previewQuestion(questionId, parameters = {}) {
  return request(`/admin/chatbot/questions/${encodeURIComponent(questionId)}/preview`, {
    method: 'POST',
    body: JSON.stringify({ parameters }),
  });
}

/* ── Platform Columns & Suggestions ────────────────────────── */
const PLATFORM_COLUMN_PATHS = {
  BASIC_PRODUCT:      '/basic-product-info/column-list',
  CHEMICAL_DIRECTORY: '/chemical-directory/column-list',
  TECHNICAL_ROUTES:   '/technical-routes/column-list',
  CHEMI_TRACKER:      '/chemi-tracker/column-list',
  GAPD:               '/gppd/column-list',
};

const PLATFORM_SUGGEST_PATHS = {
  BASIC_PRODUCT:      '/basic-product-info/advance-auto-suggestions',
  CHEMICAL_DIRECTORY: '/chemical-directory/auto-suggestions',
  TECHNICAL_ROUTES:   '/technical-routes/auto-suggestions',
};

export async function getPlatformColumns(platform) {
  const path = PLATFORM_COLUMN_PATHS[platform];
  if (!path) return [];
  try {
    const data = await request(path);
    return data?.columns || [];
  } catch {
    return [];
  }
}

export async function getPlatformSuggestions(platform, column, keyword) {
  const path = PLATFORM_SUGGEST_PATHS[platform];
  if (!path || !keyword?.trim() || keyword.trim().length < 2) return [];
  try {
    const data = await request(path, {
      method: 'POST',
      body: JSON.stringify({ column, keyword: keyword.trim() }),
    });
    return data?.suggestions || [];
  } catch {
    return [];
  }
}
